import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Box, Stack, Typography, Divider} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {RotateCw} from 'lucide-react';
import _ from 'lodash';
import {DrawerHeader, DrawerHeaderTitle} from '../common/DrawerHeader';
import {CountChip} from '../copilot/CountChip';
import {CopilotSkeleton} from '../copilot/CopilotSkeleton';
import {CopilotDisclaimer} from '../copilot/CopilotDisclaimer';
import PdfInconsistencyCard from './PdfInconsistencyCard';
import {STATUS} from './hooks/usePdfInconsistencyCheck';

const styles = {
  headingLabel: {
    color: '#162523',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '160%',
  },
  loadingButton: {
    border: '1px solid #CDD3D3',
    borderRadius: 2,
    paddingX: 2,
    paddingY: 1,
    height: 32,
  },
  noErrors: {
    header: {
      color: '#162523',
      fontSize: '14px',
      fontWeight: 500,
      paddingX: 2,
      paddingTop: 2,
      paddingBottom: 1,
    },
    text: {
      color: '#65706D',
      fontSize: '12px',
      fontWeight: 400,
      paddingX: 2,
      paddingBottom: 1,
    },
  },
  errorBox: {
    border: '1px solid #FFC738',
    borderRadius: '0.5rem',
    backgroundColor: '#FFEEC3',
  },
  errorText: {
    color: '#0F1E24',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: '1.125rem',
  },
};

const getHeadingLabel = (status, inconsistenciesData) => {
  if (status === STATUS.RUNNING) {
    return 'Reviewing your report for inconsistencies. This can take several minutes...';
  }

  if (status === STATUS.COMPLETE && inconsistenciesData) {
    if (inconsistenciesData.numItems === 0) {
      return "Our AI reviewed your report and didn't find any inconsistencies.";
    }
    return 'Our AI scanned the report for inconsistencies. Please review and comment. Your comments will be attached to the PDF after export.';
  }

  return null;
};

const PdfInconsistencyCheck = ({
  status,
  inconsistenciesData,
  error,
  onBack,
  onRerunCheck,
  onInconsistencySelect,
  onLocationSelect,
  documentId,
  onInconsistencyDismiss,
  onInconsistencyDismissError,
}) => {
  const [selectedInconsistencyId, setSelectedInconsistencyId] = useState(null);

  const isLoading = status === STATUS.RUNNING;
  const isError = status === STATUS.ERROR;
  const isComplete = status === STATUS.COMPLETE;

  const totalItems = inconsistenciesData?.numItems ?? 0;
  const totalOpenItems = inconsistenciesData?.numOpenItems ?? 0;

  const handleInconsistencySelect = useCallback(
    (inconsistency) => {
      setSelectedInconsistencyId(inconsistency.id);
      onInconsistencySelect?.(inconsistency);
    },
    [onInconsistencySelect]
  );

  const handleLocationSelect = useCallback(
    (inconsistency, item, index) => {
      onLocationSelect?.(inconsistency, item, index);
    },
    [onLocationSelect]
  );

  const renderError = () => (
    <Stack direction="column" spacing="1rem" px={1}>
      <Stack direction="row" gap="0.75rem" p="0.75rem" sx={styles.errorBox}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="#FFC738"
          height="1.25rem"
          minWidth="1.25rem"
          borderRadius="50%"
        >
          <Typography fontSize="0.75rem" fontWeight={600}>
            !
          </Typography>
        </Box>
        <Typography sx={styles.errorText}>
          {error ||
            'An error occurred while loading results. Please try again.'}
        </Typography>
      </Stack>
    </Stack>
  );

  const renderNoIssues = () => (
    <Stack direction="column">
      <Divider sx={{borderColor: '#E1E5E4'}} />
      <Typography sx={styles.noErrors.header}>
        No inconsistencies found - Nice work!
      </Typography>
      <Typography sx={styles.noErrors.text} mb={1}>
        We suggest running additional checks prior to filing since items can
        arise and our AI is capable of making mistakes.
      </Typography>
      <Divider sx={{borderColor: '#E1E5E4'}} />
    </Stack>
  );

  const renderInconsistencyCards = () => {
    if (totalOpenItems === 0) {
      return renderNoIssues();
    }

    const openItems = _.values(inconsistenciesData?.openItems || {});
    // Sort by first item's page number
    const sortedItems = _.orderBy(openItems, [
      (item) => {
        const firstLocation = item.items?.[0];
        const grounding =
          firstLocation?.cell_grounding ?? firstLocation?.grounding;
        return grounding?.page ?? 0;
      },
      (item) => {
        const firstLocation = item.items?.[0];
        const grounding =
          firstLocation?.cell_grounding ?? firstLocation?.grounding;
        return grounding?.box?.top ?? 0;
      },
    ]);

    return (
      <Box display="flex" flexDirection="column" flex={1}>
        {sortedItems.map((inconsistency) => (
          <PdfInconsistencyCard
            key={inconsistency.id}
            inconsistency={inconsistency}
            isSelected={selectedInconsistencyId === inconsistency.id}
            onSelect={handleInconsistencySelect}
            onLocationSelect={handleLocationSelect}
            documentId={documentId}
            onDismiss={onInconsistencyDismiss}
            onDismissError={onInconsistencyDismissError}
          />
        ))}
        <Divider sx={{borderColor: '#DCE2E0'}} />
      </Box>
    );
  };

  const renderContent = () => {
    if (isError) {
      return renderError();
    }

    if (isLoading) {
      return <CopilotSkeleton />;
    }

    if (!isComplete || totalItems === 0) {
      return (
        <Stack spacing={2} width="100%">
          <Stack
            direction="row"
            flex={1}
            spacing={0.5}
            justifyContent="flex-start"
            width="100%"
          >
            <CountChip
              icon={totalOpenItems ? 'info' : 'check'}
              variant={totalOpenItems ? 'error' : 'ghost'}
              label={`${totalOpenItems} issues`}
            />
          </Stack>
          <Divider sx={{borderColor: '#E1E5E4'}} />
        </Stack>
      );
    }

    return (
      <Stack direction="column" spacing="1rem" width="100%" flex={1}>
        <Stack direction="row" spacing={0.5} alignItems="center" px={1.5}>
          <CountChip
            icon={totalOpenItems ? 'info' : 'check'}
            variant={totalOpenItems ? 'error' : 'success'}
            label={`${totalOpenItems} issues`}
          />
        </Stack>
        {renderInconsistencyCards()}
      </Stack>
    );
  };

  return (
    <Stack direction="column" flex={1} height="100%">
      <DrawerHeader>
        <DrawerHeaderTitle title="Inconsistency check" onBack={onBack} />
      </DrawerHeader>

      <Stack
        direction="column"
        spacing={2}
        px={2}
        py={1}
        flex={1}
        width="100%"
        sx={{overflowY: 'auto'}}
      >
        <Stack direction="column" px={1}>
          <Typography sx={styles.headingLabel}>
            {getHeadingLabel(status, inconsistenciesData)}
          </Typography>
        </Stack>

        {renderContent()}

        <Box
          py={2}
          position="sticky"
          bottom={0}
          backgroundColor="white"
          borderTop="1px solid #E1E5E4"
        >
          <LoadingButton
            variant="outlined"
            onClick={onRerunCheck}
            sx={styles.loadingButton}
            loading={isLoading}
            disabled={isLoading}
          >
            <RotateCw size={16} />
            <Typography fontWeight={500} fontSize={12} lineHeight="160%" pl={1}>
              Run check again
            </Typography>
          </LoadingButton>
        </Box>
      </Stack>

      <CopilotDisclaimer />
    </Stack>
  );
};

PdfInconsistencyCheck.propTypes = {
  status: PropTypes.oneOf(Object.values(STATUS)).isRequired,
  inconsistenciesData: PropTypes.shape({
    items: PropTypes.object,
    openItems: PropTypes.object,
    numItems: PropTypes.number,
    numOpenItems: PropTypes.number,
  }),
  error: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onRerunCheck: PropTypes.func.isRequired,
  onInconsistencySelect: PropTypes.func,
  onLocationSelect: PropTypes.func,
  documentId: PropTypes.string,
  onInconsistencyDismiss: PropTypes.func,
  onInconsistencyDismissError: PropTypes.func,
};

export default PdfInconsistencyCheck;
